import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import * as api from "../../api";
import Section from "../Atoms/Section";
import H1 from "../Atoms/H1";
import H2 from "../Atoms/H2";
import Spinner from "../../Plateforme/Atoms/Spinner";
import BigH1 from "../Atoms/BigH1";
import BigH2 from "../Atoms/BigH2";
import BlueButton from "../Atoms/BlueButton";

// Styled components for the layout and animations
const StylesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 60px;
  margin-top: 40px;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
`;

const CahierContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const CahierTitle = styled.h3`
  font-size: 1.3rem;
  margin-bottom: 15px;
  font-weight: 600;
`;

const CahierContent = styled.div`
  display: flex;
  position: relative;
  width: 100%;
  height: 450px;
  
  &:hover .preview-page {
    transform: translateX(0);
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    height: auto;
  }
`;

const CoverImage = styled.div`
  width: 40%;
  min-width: 300px;
  position: relative;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: pointer;
  height: 100%;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    min-width: unset;
    min-height: 300px;
  }
`;

const PreviewContainer = styled.div`
  display: flex;
  flex: 1;
  position: relative;
  overflow: hidden;
  height: 100%;
  
  @media (max-width: 768px) {
    margin-top: 20px;
    min-height: 300px;
  }
`;

const PreviewPage = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  transform: translateX(-100%);
  transition: transform 0.5s ease;
  position: relative;
  z-index: 5;
  align-items: center;
  justify-content: center;
  padding: 20px;
  
  img {
    max-height: 100%;
    max-width: 100%;
    object-fit: contain;
  }
`;

// Wrapper component that combines BlueButton styling with anchor functionality
const ConsulterButtonWrapper = styled.div`
  position: absolute;
  bottom: 20px;
  right: 20px;
  z-index: 20;
  
  /* Override button styles to match the original ConsulterButton */
  ${BlueButton} {
    background-color: #0024cb;
    color: white;
    padding: 8px 16px;
    border-radius: 3px;
    text-decoration: none;
    font-weight: 500;
    text-transform: uppercase;
    font-size: 14px;
    transition: background-color 0.2s ease;
    cursor: pointer;
    
    &:hover {
      background-color: #001aa6;
    }
  }
`;

const ViewButton = styled.a`
  display: inline-block;
  background-color: #0066cc;
  color: white;
  padding: 10px 20px;
  border-radius: 4px;
  margin-top: 15px;
  text-decoration: none;
  font-weight: 500;
  text-transform: uppercase;
  font-size: 14px;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: #0055aa;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  background-color: #f5f5f5;
  border-radius: 8px;
  margin: 30px auto;
  max-width: 600px;
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  padding: 15px;
  border: 1px solid #dc3545;
  border-radius: 4px;
  margin-bottom: 20px;
`;

const IntroText = styled.p`
  max-width: 800px;
  margin: 20px auto 40px;
  text-align: center;
  color: #555;
  line-height: 1.6;
`;

const STATIC_CAHIER_3 = {
  _id: 'static-3',
  title: 'Cahier de Style #3',
  firstPageImage: '/documents/Couv.png',
  previewImage: '/documents/Apercu.png',
};

const STATIC_CAHIER_4 = {
  _id: 'static-4',
  title: 'Cahier de Style #4',
  firstPageImage: '/documents/Couv4.png',
  previewImage: '/documents/Couv5.png',
};

// Members-only: PDFs are served by the API to logged-in users (GET /cdc/pdf/:id, requireAuth)
const cahierPdfUrl = (cdc) => `${process.env.REACT_APP_API_BASE_URL}/cdc/pdf/${cdc._id}`;

const ActualitesCahierDeStyle = ({ fullUser, fullUserLoading }) => {
  const navigate = useNavigate();
  const [cahiersDeStyle, setCahiersDeStyle] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCahiersDeStyle = async () => {
      setLoading(true);
      try {
        const res = await api.GetAllCahierDeStyle();
        // DB entries first, then static #3
        setCahiersDeStyle([...res.data, STATIC_CAHIER_3, STATIC_CAHIER_4]);
        setError(null);
      } catch (error) {
        console.error("Erreur lors de la récupération des cahiers de style:", error);
        // On any API error, still show static entry
        setCahiersDeStyle([STATIC_CAHIER_3, STATIC_CAHIER_4]);
        setError(null);
      } finally {
        setLoading(false);
      }
    };

    fetchCahiersDeStyle();
  }, []);

  const openCahier = (cdc) => {
    if (!fullUser) {
      navigate('/login?redirect=/cahiersdestyle');
      return;
    }
    window.open(cahierPdfUrl(cdc), '_blank', 'noopener,noreferrer');
  };

  if (loading || fullUserLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "300px" }}>
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <Section className="padding">
        <ErrorMessage>
          {error}
        </ErrorMessage>
      </Section>
    );
  }

  return (
    <div>
      <Section className="padding">
        <BigH2 style={{marginBottom:"40px"}}>Cahiers de style</BigH2>
        <IntroText>
          {!fullUser && (
            <>Les cahiers de style sont réservés aux membres de la plateforme. Connectez-vous ou créez votre compte pour les consulter et les télécharger.</>
          )}
          {/* Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. */}
        </IntroText>

        {cahiersDeStyle.length === 0 ? (
          <EmptyState>
            <H2>Aucun cahier de style disponible pour le moment</H2>
            <p>Veuillez consulter cette page ultérieurement pour découvrir nos nouveaux cahiers de style.</p>
          </EmptyState>
        ) : (
          <StylesList>
            {cahiersDeStyle.map((cdc, index) => (
              <CahierContainer key={cdc._id}>
                <CahierTitle>#{index + 1} - {cdc.title}</CahierTitle>
                <CahierContent>
                  <CoverImage>
                    <img src={cdc.firstPageImage} alt={cdc.title} />
                  </CoverImage>
                  <PreviewContainer>
                    <PreviewPage className="preview-page">
                      {cdc.previewImage ? (
                        <img src={cdc.previewImage} alt={`Aperçu de ${cdc.title}`} />
                      ) : cdc.firstPageImage ? (
                        <img src={cdc.firstPageImage} alt={`Aperçu de ${cdc.title}`} />
                      ) : (
                        <p>Aucun aperçu disponible</p>
                      )}
                      {/* Using BlueButton with a click handler to open PDF */}
                      <ConsulterButtonWrapper>
                        <BlueButton
                          onClick={() => openCahier(cdc)}
                          title={fullUser ? `Consulter ${cdc.title}` : 'Se connecter pour consulter'}
                        >
                          {fullUser ? 'Consulter →' : 'Se connecter pour consulter →'}
                        </BlueButton>
                      </ConsulterButtonWrapper>
                    </PreviewPage>
                  </PreviewContainer>
                </CahierContent>
              </CahierContainer>
            ))}
          </StylesList>
        )}
      </Section>
    </div>
  );
};

export default ActualitesCahierDeStyle;